import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { navigate } from "gatsby";
import { Button } from "../Button";
import { RCL as useTranslation } from "../RCL";
import Logout from "../../templates/user-account/components/Logout";
import { userAccountRoutingPaths } from "../../templates/user-account/config";
import { removeCookie } from "../../utils/helper";
import { useUser } from "../../context";
import * as style from "./OktaHeader.module.scss";

const OktaMenu = ({
	locale,
	isUserLoggedIn,
	isOktaMenuOpen,
	setLoginOpen,
	setWidgetAction,
	setOktaMenuToOpen,
	isAgent,
}) => {
	const { setProfileTab } = useUser();
	const language = locale?.toLowerCase()?.substring(0, 2);
	const { base, login, profile } = userAccountRoutingPaths[language];
	const signinRoute = `/${language}/${base}/${login}`;
	const profileRoute = `/${language}/${base}/${profile}`;

	const dictionary = {
		"okta-header-menu-access-profile": useTranslation({
			searchKey: "okta-header-menu-access-profile",
		}),
		"okta-header-menu-account-information": useTranslation({
			searchKey: "okta-header-menu-account-information",
		}),
		"okta-header-menu-heading": useTranslation({
			searchKey: "okta-header-menu-heading",
		}),
		"okta-header-menu-heading-v2": useTranslation({
			searchKey: "okta-header-menu-heading-v2",
		}),
		"okta-header-menu-message": useTranslation({
			searchKey: "okta-header-menu-message",
		}),
		"okta-header-menu-message-v2": useTranslation({
			searchKey: "okta-header-menu-message-v2",
		}),
		"okta-header-menu-sign-in": useTranslation({
			searchKey: "okta-header-menu-sign-in",
		}),
		"okta-header-menu-create-account": useTranslation({
			searchKey: "okta-header-menu-create-account",
		}),
	};

	// Menu trapping
	const oktaMenuRef = useRef(null);
	const focusableElements = oktaMenuRef.current?.querySelectorAll(
		"button, [href], [tabindex]:not([tabindex='-1'])"
	);

	useEffect(() => {
		const handleMenuFocus = event => {
			let newElementItem = null;
			let counter = 0;
			let thisElementPosition = null;

			if (focusableElements) {
				for (let feItem = 0; feItem < focusableElements.length; feItem++) {
					const fe = focusableElements[feItem];

					if (fe === event.target) {
						thisElementPosition = counter;
					}
					counter += 1;
				}

				if (event.key === "ArrowDown") {
					event.preventDefault();

					if (
						focusableElements.length - 1 === thisElementPosition ||
						thisElementPosition === null
					) {
						// eslint-disable-next-line prefer-destructuring
						newElementItem = focusableElements[0];
					} else {
						newElementItem = focusableElements[thisElementPosition + 1];
					}

					if (newElementItem) {
						newElementItem.focus();
					}
				} else if (event.key === "ArrowUp") {
					event.preventDefault();

					if (thisElementPosition <= 0) {
						newElementItem = focusableElements[focusableElements.length - 1];
					} else {
						newElementItem = focusableElements[thisElementPosition - 1];
					}

					if (newElementItem) {
						newElementItem.focus();
					}
				}
			}
		};

		document.addEventListener("keydown", handleMenuFocus, true);

		return () => {
			document.removeEventListener("keydown", handleMenuFocus, true);
		};
	}, [focusableElements, isOktaMenuOpen]);

	const resetCookies = () => {
		/* Remove MBP tracking cookie */
		removeCookie("sso-destination", "/", window.location.hostname);
		removeCookie("sso-type", "/", window.location.hostname);
	};

	const loginToDirectAccount = (event, destination) => {
		resetCookies();

		if (window.location.href.indexOf(destination) > -1) {
			window.location.href = `${destination}`;
		} else {
			setLoginOpen(true);
			setWidgetAction("");
		}
	};

	if (isUserLoggedIn) {
		return (
			<div className={style.loggedIn}>
				<ul className={style.accountMenu} ref={oktaMenuRef}>
					{!isAgent && (
						<>
							<li className={style.listItem}>
								<Button
									noStyle
									icon="my-bookings"
									className={style.myTrips}
									onClick={() => {
										setOktaMenuToOpen(false);
										setProfileTab("my-trips");
										navigate(profileRoute);
									}}>
									<span className={style.textIndent}>
										{dictionary["okta-header-menu-access-profile"]}
									</span>
								</Button>
							</li>
							<li className={style.listItem}>
								<Button
									noStyle
									icon="my-account"
									className={style.myAccount}
									onClick={() => {
										setOktaMenuToOpen(false);
										setProfileTab("profile");
										navigate(profileRoute);
									}}>
									<span className={style.textIndent}>
										{dictionary["okta-header-menu-account-information"]}
									</span>
								</Button>
							</li>

							<li className={style.divider} />
						</>
					)}
					<li className={style.listItem}>
						<Logout
							language={language}
							onClickHandler={() => {
								resetCookies();
								// Remove all traces of session management
								localStorage.removeItem("okta-cache-storage");
								localStorage.removeItem("okta-shared-transaction-storage");
								localStorage.removeItem("okta-token-storage");
							}}
						/>
					</li>
				</ul>
			</div>
		);
	}
	return (
		<div className={style.loggedOut}>
			<span className={style.heading}>{dictionary["okta-header-menu-heading-v2"]}</span>
			<div>{dictionary["okta-header-menu-message-v2"]}</div>
			<div className={style.widgetButtons}>
				<div className={(style.signin, style.buttonWrapper)}>
					<Button
						to={signinRoute}
						full
						onClick={event => loginToDirectAccount(event.nativeEvent, signinRoute)}>
						{dictionary["okta-header-menu-sign-in"]}
					</Button>
				</div>
				<div className={(style.signin, style.buttonWrapper)}>
					<Button
						onClick={() => {
							setLoginOpen(true);
							setWidgetAction("register");
						}}
						full
						outline={true}>
						{dictionary["okta-header-menu-create-account"]}
					</Button>
				</div>
			</div>
		</div>
	);
};

export default OktaMenu;

OktaMenu.propTypes = {
	locale: PropTypes.string,
	isUserLoggedIn: PropTypes.bool,
	isOktaMenuOpen: PropTypes.bool,
	setLoginOpen: PropTypes.func.isRequired,
	setWidgetAction: PropTypes.func.isRequired,
	setOktaMenuToOpen: PropTypes.func.isRequired,
	isAgent: PropTypes.bool,
};

OktaMenu.defaultProps = {
	locale: "en",
	isUserLoggedIn: false,
	isOktaMenuOpen: false,
	isAgent: false,
};
